import {
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  EditSignalRequest,
  LanguageNameSpaces,
  OrderStatus,
} from "@shared/types";
import { EditSignalContext } from "features/signals/EditSignal/EditSignal.context";
import { useSelector } from "react-redux";
import {
  selectSignalTargetsData,
  selectSignalTargetsPrecision,
} from "@shared/slices/signalExtraDetails.slice";
import {
  confirmWithCallback,
  displayResultMessage,
  openMessage,
  showUnsavedChangesWarning,
} from "utils/misc";
import { validateEditSignalTargets } from "features/signals/EditSignal/utils";
import { getRouteWithoutPath, getSignalExtraDetailsRoute } from "utils/routes";
import {
  ADMIN_SIGNALS_PATH,
  SIGNAL_EXTRA_DETAILS_PATH,
} from "common/constants";
import { useRouteMatch } from "react-router-dom";
import useGeneralConfiguration from "features/configuration/GeneralConfiguration/useGeneralConfiguration";
import useMemoizedSelector from "@shared/utils/hooks/useMemoizedSelector";
import { skipToken } from "@reduxjs/toolkit/query";
import Modal from "common/Modal/Modal";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import { OrderTypes } from "@shared/features/trades/common/types";
import { SIGNAL_EXTRA_DETAILS_TABS } from "@shared/common/constants";
import { SignalDetailsContext } from "@shared/features/signals/common/SignalDetails.context";
import {
  getTargetsAnnotations,
  isEditableTargetZone,
} from "@shared/features/signals/common/utils";
import {
  EditTargetData,
  EditTargetOrZone,
  ValidationTargetData,
  ValidationTargetsData,
} from "@shared/features/signals/EditSignal/types";
import { useEditSignalTargets } from "@shared/features/signals/EditSignal/useEditSignalTargets";
import { getMinAndMaxPrices } from "@shared/features/tradingTerminal/common/utils";
import { sharedLib } from "@shared/services/shared.service";
import { validateSaveOrder } from "@shared/features/trades/common/utils";
import { useTheme } from "styled-components";
import { useRefValue } from "@shared/utils/hooks/misc.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";

const EditSignalWrapper = ({ children }: PropsWithChildren) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const theme = useTheme();
  const {
    isEdit,
    signalId,
    tab,
    baseArgs,
    exchangeSignalArgs,
    precisionConfig,
  } = useContext(SignalDetailsContext)!;
  const [valuesModified, setValuesModified] = useState(false);
  const valuesModifiedRef = useRefValue(valuesModified);
  const [confirmModal, confirmModalContextHolder] = Modal.useModal();

  const isAdminSignals = Boolean(useRouteMatch(ADMIN_SIGNALS_PATH));
  const precision = useSelector(
    selectSignalTargetsPrecision(exchangeSignalArgs)
  );
  const onValuesModified = () => setValuesModified(true);
  const [editSignal, { isLoading: editSignalLoading }] =
    sharedLib.sharedApi.signals.useEditSignalMutation();

  const { data: configurationData } = useMemoizedSelector({
    selectorCreator:
      sharedLib.sharedApi.signals.endpoints.getSignalExtraInfoConfiguration
        .select,
    args: baseArgs
      ? { signal_id: signalId, for_admin: baseArgs.for_admin }
      : skipToken,
  });

  const [
    editSignalConfiguration,
    { isLoading: editSignalConfigurationLoading },
  ] = sharedLib.sharedApi.signals.useEditSignalConfigurationMutation();
  const signalData = useSelector(selectSignalTargetsData(exchangeSignalArgs));
  const currentPrice = signalData?.current_price || 0;

  const {
    entryTargets,
    takeProfitTargets,
    stopTargets,
    setEntryTargets,
    setTakeProfitTargets,
    setStopTargets,
    getTargetsByType,
    resetTargetsChanges,
    updateTarget,
    moveTargetToCurrentPrice,
    moveStopTargetToBreakEvenPrice,
    addTarget,
    cancelTarget,
    setTargetsByType,
  } = useEditSignalTargets({
    targetsArgs: exchangeSignalArgs,
    onValuesModified,
    isShort: signalData?.reverse,
    isBreakout: signalData?.breakout,
  });

  const { historyPush } = useHistoryWithQuery();

  const {
    generalConfiguration,
    configurationModified,
    cancelChanges: cancelConfigurationChanges,
    modifiedConfiguration,
  } = useGeneralConfiguration({
    //@ts-ignore
    configuration: configurationData?.data,
    editable: isEdit && tab === SIGNAL_EXTRA_DETAILS_TABS.CONFIGURATION,
    showOverrides: false,
    precisionConfig: precision,
    spacing: 30,
    useCard: false,
    addEditWarningMessage: isAdminSignals,
  });

  useEffect(() => {
    if (configurationModified) {
      setValuesModified(true);
    }
  }, [configurationModified]);

  const resetChanges = () => {
    if (tab === SIGNAL_EXTRA_DETAILS_TABS.TARGETS) {
      resetTargetsChanges();
    } else if (tab === SIGNAL_EXTRA_DETAILS_TABS.CONFIGURATION) {
      cancelConfigurationChanges();
    }
    setValuesModified(false);
  };

  const onCancelChangesClick = async (onCancelChangesCallback?: () => void) => {
    const onCancelChanges = () => {
      resetChanges();
      onCancelChangesCallback?.();
    };
    if (valuesModifiedRef.current) {
      const userWantsToStay = await showUnsavedChangesWarning(confirmModal);
      if (!userWantsToStay) onCancelChanges();
    } else {
      onCancelChanges();
    }
  };

  const saveConfigurationChanges = async () => {
    if (!valuesModified) {
      openMessage(t("edit_signal.errors.no_values_modified"), "warning");
    } else if (signalId && modifiedConfiguration) {
      await displayResultMessage(
        editSignalConfiguration({
          signal_id: signalId,
          configuration: modifiedConfiguration,
        }).unwrap(),
        t("edit_signal.success.configuration_edited"),
        t("edit_signal.errors.failed_to_edit_configuration"),
        5
      );
      exitEditMode();
    }
  };

  const convertTargetToRequestSections = (targets: EditTargetOrZone[]) => {
    const validTargets = targets.filter(
      (target) =>
        target.edit_status !== OrderStatus.CANCELLED ||
        target.status === OrderStatus.CANCELLED
    );

    const prices = validTargets
      .flatMap((target) => target.edit_target_price)
      .map((price) => parseFloat(price));
    const section: EditSignalRequest["buy_section"] = [];
    let zone: EditSignalRequest["buy_zone"] | [] = prices.length
      ? [Math.min(...prices), Math.max(...prices)]
      : [];

    validTargets.forEach((target, index) => {
      if (!isEditableTargetZone(target)) {
        section.push({
          price: parseFloat(target.edit_target_price),
          ratio: target.edit_ratio,
          is_closed: !target.edit_status?.toLowerCase().includes("pending"),
          number: index + 1,
        });
      }
    });
    return { section, zone };
  };

  const getEditSignalRequest = (): EditSignalRequest => {
    const buySection = convertTargetToRequestSections(entryTargets);
    const takeProfitSection = convertTargetToRequestSections(takeProfitTargets);
    const stopSection = convertTargetToRequestSections(stopTargets);
    return {
      signal_id: signalId!,
      buy_section: buySection.section,
      sell_section: takeProfitSection.section,
      stop_section: stopSection.section,
      buy_zone: buySection.zone,
      sell_zone: takeProfitSection.zone,
      stop_zone: stopSection.zone,
    };
  };

  const convertEditTargetsToValidation = (
    targets: EditTargetOrZone[]
  ): ValidationTargetData[] => {
    const getTargetPercent = (target: EditTargetOrZone) => {
      const isTarget = !isEditableTargetZone(target);
      if (isTarget && target.edit_ratio !== null) {
        return target.edit_ratio * 100;
      }
    };

    return targets
      .filter((target) => target.edit_status !== OrderStatus.CANCELLED)
      .map((target) => {
        const isZone = isEditableTargetZone(target);
        return {
          price: isZone
            ? [
                parseFloat(target.edit_target_price[0]),
                parseFloat(target.edit_target_price[1]),
              ]
            : parseFloat(target.edit_target_price),
          percent: getTargetPercent(target),
          status: target.edit_status,
          isZone,
        };
      });
  };

  const validationTargetsData: ValidationTargetsData = {
    [OrderTypes.ENTRY]: convertEditTargetsToValidation(entryTargets),
    [OrderTypes.TAKE_PROFIT]: convertEditTargetsToValidation(takeProfitTargets),
    [OrderTypes.STOP]: convertEditTargetsToValidation(stopTargets),
  };

  const priceLimits = getMinAndMaxPrices(
    currentPrice,
    precision?.price || 0,
    undefined,
    signalData?.limits
  );

  const errors = validateEditSignalTargets(
    validationTargetsData,
    !!signalData?.reverse,
    !!signalData?.breakout,
    currentPrice,
    priceLimits.min,
    priceLimits.max
  );

  const exitEditMode = (newSignalId?: number) => {
    const route = getRouteWithoutPath(
      location.pathname,
      SIGNAL_EXTRA_DETAILS_PATH
    );
    const _signalId = newSignalId || signalId;
    if (_signalId && tab) {
      historyPush(getSignalExtraDetailsRoute(route, _signalId, tab, false));
    }
    resetChanges();
  };

  const onSaveChangesClick = () => {
    if (tab === SIGNAL_EXTRA_DETAILS_TABS.TARGETS) {
      saveTargetsChanges().catch(() => {});
    } else if (tab === SIGNAL_EXTRA_DETAILS_TABS.CONFIGURATION) {
      saveConfigurationChanges().catch(() => {});
    }
  };

  const saveTargetsChanges = async () => {
    const isValid = validateSaveOrder(
      true,
      errors,
      valuesModified,
      openMessage
    );
    if (isValid) {
      const editSignalRequest = editSignal(getEditSignalRequest()).unwrap();
      try {
        const editSignalResponse = await displayResultMessage(
          editSignalRequest,
          t("edit_signal.success.signal_edited"),
          t("edit_signal.errors.failed_to_edit"),
          5
        );
        if (editSignalResponse) exitEditMode(editSignalResponse.new_signal_id);
      } catch {}
    }
  };

  const targetsAnnotations = useMemo(() => {
    if (!precisionConfig) return [];
    return getTargetsAnnotations(
      theme,
      entryTargets,
      takeProfitTargets,
      stopTargets,
      precisionConfig,
      isEdit,
      currentPrice
    );
  }, [
    theme,
    entryTargets,
    takeProfitTargets,
    stopTargets,
    precisionConfig,
    isEdit,
    currentPrice,
  ]);

  return (
    <EditSignalContext.Provider
      value={{
        currentPrice,
        precisionConfig: precision,
        entryTargets,
        takeProfitTargets: takeProfitTargets as EditTargetData[],
        stopTargets: stopTargets as EditTargetData[],
        setEntryTargets,
        setTakeProfitTargets,
        setStopTargets,
        onValuesModified,
        getTargetsByType,
        signalData,
        updateTarget,
        moveTargetToCurrentPrice,
        setTargetsByType,
        moveStopTargetToBreakEvenPrice,
        onSaveChangesClick,
        confirmWithCallback: (message: string) =>
          confirmWithCallback(confirmModal, message),
        onCancelChangesClick,
        addTarget,
        cancelTarget,
        editSignalLoading,
        isEdit,
        targetsErrors: errors,
        generalConfiguration,
        editSignalConfigurationLoading,
        targetsAnnotations,
      }}
    >
      {children}
      {confirmModalContextHolder}
    </EditSignalContext.Provider>
  );
};

export default EditSignalWrapper;
